import { Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { IdentityMismatchView } from "@/components/onboarding/identity-mismatch-view"
import PublicLayout from "@/layouts/public-layout"

export interface IdentityMismatchPageBrand {
  name: string
  logo_url: string | null
  locale: "en" | "fr"
}

export interface IdentityMismatchPageProps {
  brand: IdentityMismatchPageBrand
  /** TikTok-returned handle from the OAuth callback (the handle that
   *  didn't match the candidate). Surfaced in the body copy. */
  returned_handle: string
  /**
   * Server-resolved support `mailto:` URL — falls back to
   * `credentials.support.email` if `brand.support_email` is blank
   * (resolved server-side per UX §12.1; FE never decides).
   */
  support_url: string
}

/**
 * IdentityMismatch — dedicated dead-end page for the FIRST-time TikTok
 * mismatch (Flow E', UX Spec §1).
 *
 * Server flips the membership to `mismatch_quarantine`, clears
 * `users.active_brand_id` if it pointed to this brand, and 303s here
 * (BE TRD §Approach + §Key Decisions). NO retry CTA from creator side
 * — brand re-issues invite.
 *
 * Renders the shared `<IdentityMismatchView>` with
 * `context="post_oauth"` per UX §12.1. Same component is also used
 * inside `pages/Public/InviteLanding.tsx` for the second-brand
 * `mismatch_context="recognised"` variant — single source of truth for
 * the dead-end UX.
 *
 * **testid contract** (UX Spec §12.1 + FE TRD §testid):
 *   - `identity-mismatch-page` on the page wrapper
 *   - `tiktok-failure-identity-mismatch` for OKS-23 Playwright continuity
 *     (the dedicated dead-end is reachable from the wizard step 1 OAuth
 *      callback path; same testid as the OKS-23 same-screen variant
 *      that's been DROPPED here per FE TRD v2)
 *   - `invite-landing-error-mismatch` (rendered by IdentityMismatchView)
 *     with `data-mismatch-context="post_oauth"`
 */
export default function IdentityMismatch({
  brand,
  returned_handle,
  support_url,
}: IdentityMismatchPageProps) {
  const { t } = useTranslation()
  return (
    <PublicLayout>
      <Head>
        <title>{t("wizard.identity_mismatch.head_title")}</title>
      </Head>
      <div
        data-testid="identity-mismatch-page"
        className="bg-background flex min-h-svh flex-col"
      >
        {/*
          Slim brand chrome — same shape as MinimalSignup so the dead-end
          isn't a sudden visual context shift. Brand surfaces as DATA.
        */}
        <header className="border-border/40 border-b px-6 py-3">
          <div className="flex items-center gap-2">
            {brand.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.name}
                className="size-7 rounded-md object-cover"
                data-testid="identity-mismatch-page-brand-logo"
              />
            ) : (
              <span
                aria-hidden="true"
                data-testid="identity-mismatch-page-brand-logo"
                className="bg-foreground/10 flex size-7 items-center justify-center rounded-md text-xs font-semibold"
              >
                {brand.name.slice(0, 2).toUpperCase()}
              </span>
            )}
            <span
              className="text-foreground text-sm font-medium"
              data-testid="identity-mismatch-page-brand-name"
            >
              {brand.name}
            </span>
          </div>
        </header>
        <main
          role="main"
          tabIndex={-1}
          className="mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-6 py-10 outline-none"
          data-testid="tiktok-failure-identity-mismatch"
        >
          <IdentityMismatchView
            context="post_oauth"
            handle={returned_handle}
            cta_url={support_url}
            brand_name={brand.name}
          />
        </main>
      </div>
    </PublicLayout>
  )
}
